import { Button, Filter, Input, Search, Select, X } from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

interface PatientsFilterProps {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  showFilters: boolean;
  setShowFilters: (showFilters: boolean) => void;
  filterGender: string;
  setFilterGender: (filterGender: string) => void;
  filterAgeRange: string;
  setFilterAgeRange: (filterAgeRange: string) => void;
  debouncedSearchTerm: string;
}

export const PatientsFilter: React.FC<PatientsFilterProps> = ({
  searchTerm,
  setSearchTerm,
  showFilters,
  setShowFilters,
  filterGender,
  setFilterGender,
  filterAgeRange,
  setFilterAgeRange,
  debouncedSearchTerm,
}) => {
  const hasActiveFilters = filterGender || filterAgeRange;
  const { t } = useTranslation();
  const clearFilters = () => {
    setFilterGender("");
    setFilterAgeRange("");
  };

  return (
    <div className="p-6 card-base">
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:space-x-4 lg:space-y-0">
        <div className="flex-1">
          <div className="relative">
            <Input
              type="text"
              placeholder={t("patient.manage.filters.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="pr-10 w-full input-base"
            />
            {searchTerm.trim().length > 0 && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
              </div>
            )}
          </div>
          {searchTerm !== debouncedSearchTerm &&
            searchTerm.trim().length > 0 && (
              <p className="mt-1 text-xs text-neutral-500">{t("patient.manage.filters.searching")}</p>
            )}
        </div>
        <div className="flex space-x-4">
          <Button
            variant={showFilters ? "ews-primary" : "ghost"}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-2 w-4 h-4" />
            {t("appointment.manage.filters")}
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-ews-primary-100 text-ews-primary-800 rounded-full">
                {[filterGender, filterAgeRange].filter(Boolean).length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="flex items-end gap-4 mt-6">
          <div className="flex-1 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              label={t("common.fieldLabels.gender")}
              options={[
                { value: "", label: t("patient.manage.filters.allGenders") },
                { value: "MAN", label: t("gender.MAN") },
                { value: "WOMAN", label: t("gender.WOMAN") },
              ]}
              value={filterGender}
              onChange={(value) => setFilterGender(value as string)}
              placeholder={t("patient.manage.filters.selectGender")}
            />

            <Select
              label={t("patient.manage.filters.ageRange")}
              options={[
                { value: "", label: t("patient.manage.filters.allAges") },
                { value: "0-18", label: t("patient.manage.filters.ageRanges.0-18") },
                { value: "19-30", label: t("patient.manage.filters.ageRanges.19-30") },
                { value: "31-50", label: t("patient.manage.filters.ageRanges.31-50") },
                { value: "51-65", label: t("patient.manage.filters.ageRanges.51-65") },
                { value: "65+", label: t("patient.manage.filters.ageRanges.65+") },
              ]}
              value={filterAgeRange}
              onChange={(value) => setFilterAgeRange(value as string)}
              placeholder={t("patient.manage.filters.selectAgeRange")}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              <X className="mr-2 w-4 h-4" />
              {t("patient.manage.filters.clear")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
